<template>
  <transition name="qp-fade">
    <div
      v-if="visible"
      class="bg-white border border-gray-200 rounded-lg shadow-xl mt-1 overflow-hidden"
      @mousedown.stop
    >
      <!-- Loading state -->
      <div v-if="loading" class="flex items-center justify-center py-6">
        <svg class="animate-spin h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
        </svg>
        <span class="ml-2 text-sm text-gray-400">{{ t('searchForm.findingNearbyCities') }}</span>
      </div>

      <!-- Content -->
      <template v-else>
        <!-- Locate Me button (FROM, no city selected) -->
        <button
          v-if="mode === 'from' && showLocateMe"
          type="button"
          @click="$emit('locate-me')"
          class="w-full flex items-center gap-2.5 px-4 py-3 text-sm text-blue-600 hover:bg-blue-50 transition-colors border-b border-gray-100"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path stroke-linecap="round" stroke-linejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <span class="font-semibold">{{ t('searchForm.locateMe') }}</span>
          <span class="text-xs text-gray-400 ml-auto">{{ t('searchForm.usesGps') }}</span>
        </button>

        <!-- Continent buttons (TO, nothing selected) -->
        <div v-if="mode === 'to' && continents.length > 0" class="px-3 pt-3 pb-2">
          <p class="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2 px-1">{{ t('searchForm.continents') }}</p>
          <div class="flex flex-wrap gap-1.5">
            <button
              v-for="cont in continents"
              :key="cont.code"
              type="button"
              @click.stop.prevent="$emit('select-item', { id: cont.code, display: cont.name, type: 'continent', code: cont.code })"
              class="px-3 py-1.5 text-xs font-semibold rounded-full border border-gray-200 text-gray-600 hover:border-green-400 hover:text-green-700 hover:bg-green-50 transition-all"
            >
              {{ cont.name }}
            </button>
          </div>
        </div>

        <!-- Radius slider (shown when nearby lookups are active) -->
        <div v-if="showRadius" class="px-4 pt-3 pb-1">
          <div class="flex items-center justify-between mb-1">
            <span class="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{{ t('searchForm.searchRadius') }}</span>
            <span class="text-xs font-semibold text-gray-600">{{ useMiles ? Math.round(localRadius * 0.6214) + ' mi' : localRadius + ' km' }}</span>
          </div>
          <input
            type="range"
            min="100"
            max="400"
            step="50"
            v-model.number="localRadius"
            @change="$emit('update:radius', localRadius)"
            class="w-full qp-slider"
            :class="mode === 'from' ? 'qp-slider-blue' : 'qp-slider-green'"
          />
        </div>

        <!-- Nearby cities list -->
        <div v-if="cities.length > 0" class="px-3 pb-2" :class="{ 'pt-3': !(mode === 'to' && continents.length > 0) && !showRadius }">
          <div class="flex items-center justify-between mb-2 px-1">
            <p class="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
              {{ mode === 'from' ? t('searchForm.nearbyCities') : t('searchForm.nearbyDestinations') }}
            </p>
            <button
              @click.stop.prevent="addAll"
              class="text-[10px] font-semibold hover:underline transition-colors"
              :class="mode === 'from' ? 'text-blue-600 hover:text-blue-700' : 'text-green-600 hover:text-green-700'"
              type="button"
            >{{ t('searchForm.addAll') }}</button>
          </div>
          <div class="flex flex-col max-h-64 overflow-y-auto custom-scrollbar">
            <button
              v-for="city in cities"
              :key="city.code"
              type="button"
              @click.stop.prevent="toggleCity(city)"
              class="flex items-center gap-2 px-2 py-2 text-sm text-gray-700 rounded-md hover:bg-gray-50 transition-colors text-left"
              :class="isSelected(city.code) ? (mode === 'from' ? 'bg-blue-50' : 'bg-green-50') : ''"
            >
              <span
                class="w-5 h-5 flex items-center justify-center rounded-full flex-shrink-0 transition-colors"
                :class="isSelected(city.code) ? (mode === 'from' ? 'bg-blue-100 text-blue-500' : 'bg-green-100 text-green-500') : 'bg-gray-100 text-gray-400'"
              >
                <svg v-if="isSelected(city.code)" xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3"><path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" /></svg>
                <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path stroke-linecap="round" stroke-linejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </span>
              <span class="font-medium">{{ cityName(city, locale) }}</span>
              <span v-if="city.distance_km" class="text-[11px] text-gray-400 ml-auto text-right leading-tight">
                {{ useMiles ? Math.round(city.distance_km * 0.6214) + ' mi' : city.distance_km + ' km' }}
                <span v-if="city.from_code" class="block text-[9px] text-gray-400">{{ t('searchForm.fromCode') }} {{ city.from_code }}</span>
              </span>
              <span v-else-if="city.country" class="text-xs text-gray-400 ml-auto">{{ city.country }}</span>
            </button>
          </div>
        </div>

        <!-- Search on map (TO only) -->
        <button
          v-if="mode === 'to'"
          type="button"
          @click.stop.prevent="$emit('open-map')"
          class="w-full flex items-center gap-2.5 px-4 py-3 text-sm text-gray-500 hover:bg-gray-50 transition-colors border-t border-gray-100"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span class="font-medium">{{ t('searchForm.searchOnMap') }}</span>
        </button>

        <!-- Empty state -->
        <div v-if="!loading && cities.length === 0 && showRadius" class="px-4 py-3 text-sm text-gray-400 text-center">
          {{ t('searchForm.noCitiesWithin') }} {{ useMiles ? Math.round(radius * 0.6214) + ' mi' : radius + ' km' }}
        </div>
        <div v-else-if="!loading && cities.length === 0 && continents.length === 0 && !showLocateMe && !showRadius" class="px-4 py-4 text-sm text-gray-400 text-center">
          {{ t('searchForm.noSuggestionsAvailable') }}
        </div>
      </template>
    </div>
  </transition>
</template>

<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { cityName } from '@/utils/cityName';

const props = defineProps({
  visible: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
  mode: { type: String, default: 'from' }, // 'from' or 'to'
  showLocateMe: { type: Boolean, default: false },
  cities: { type: Array, default: () => [] },
  continents: { type: Array, default: () => [] },
  showRadius: { type: Boolean, default: false },
  radius: { type: Number, default: 150 },
  selectedCodes: { type: Array, default: () => [] }, // codes of already-selected cities
  useMiles: { type: Boolean, default: false },
});

const emit = defineEmits(['select-item', 'locate-me', 'open-map', 'update:radius', 'add-all-done']);
const { locale, t } = useI18n();

// Local copy of radius so slider displays instantly without waiting for parent re-fetch
const localRadius = ref(props.radius);
watch(() => props.radius, (v) => { localRadius.value = v; });

const isSelected = (code) => props.selectedCodes.includes(code);

function toggleCity(city) {
  emit('select-item', { id: city.code, display: cityName(city, locale.value), type: 'city', code: city.code });
  // NOTE: parent decides whether to add or remove; dropdown stays open
}

function addAll() {
  props.cities.forEach(city => {
    if (!isSelected(city.code)) {
      emit('select-item', { id: city.code, display: cityName(city, locale.value), type: 'city', code: city.code });
    }
  });
  emit('add-all-done');
}
</script>

<style scoped>
.qp-fade-enter-active, .qp-fade-leave-active { transition: opacity 0.15s ease, transform 0.15s ease; }
.qp-fade-enter-from, .qp-fade-leave-to { opacity: 0; transform: translateY(-4px); }

.qp-slider {
  -webkit-appearance: none;
  appearance: none;
  height: 4px;
  border-radius: 9999px;
  background: #e5e7eb;
  outline: none;
  cursor: pointer;
  touch-action: pan-x;
}
.qp-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 16px;
  height: 16px;
  border-radius: 9999px;
  background: #fff;
  border: 2px solid;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
  cursor: pointer;
}
.qp-slider::-moz-range-thumb {
  width: 16px;
  height: 16px;
  border-radius: 9999px;
  background: #fff;
  border: 2px solid;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
  cursor: pointer;
}
.qp-slider-blue::-webkit-slider-thumb { border-color: #3b82f6; }
.qp-slider-blue::-moz-range-thumb { border-color: #3b82f6; }
.qp-slider-green::-webkit-slider-thumb { border-color: #22c55e; }
.qp-slider-green::-moz-range-thumb { border-color: #22c55e; }

.custom-scrollbar::-webkit-scrollbar { width: 4px; }
.custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 9999px; }
.custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #9ca3af; }
</style>
